<template>
  <div class="p-6 max-w-full">
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Órdenes de Producción</h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm">POS2 — Gestión de órdenes</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 flex flex-wrap gap-3 items-end">
      <div class="flex-1 min-w-48">
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Buscar</label>
        <input
          v-model="filtros.buscar"
          @keyup.enter="cargar(1)"
          type="text"
          placeholder="Número orden, folio, cliente..."
          class="w-full px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Estado</label>
        <select v-model="filtros.estado" @change="cargar(1)" class="px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
          <option value="">Todos</option>
          <option value="activa">Activa</option>
          <option value="en_proceso">En Proceso</option>
          <option value="completada">Completada</option>
          <option value="cancelada">Cancelada</option>
        </select>
      </div>
      <button @click="cargar(1)" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
      <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 transition">Limpiar</button>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div v-if="cargando" class="py-16 text-center text-gray-400">Cargando...</div>
      <div v-else-if="ordenes.length === 0" class="py-16 text-center text-gray-400">Sin órdenes de producción</div>
      <table v-else class="w-full text-sm text-left">
        <thead class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">
          <tr>
            <th class="px-4 py-3">N° Orden</th>
            <th class="px-4 py-3">Pedido</th>
            <th class="px-4 py-3">Cliente</th>
            <th class="px-4 py-3">Vendedor</th>
            <th class="px-4 py-3">Fecha Entrega</th>
            <th class="px-4 py-3">Estado</th>
            <th class="px-4 py-3">Nota</th>
            <th class="px-4 py-3">Creada</th>
            <th class="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="op in ordenes"
            :key="op.id"
            class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            <td class="px-4 py-3 font-mono font-semibold text-violet-700 dark:text-violet-400">{{ op.numero_orden }}</td>
            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">{{ op.pedido?.folio ?? '—' }}</td>
            <td class="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{{ op.pedido?.cliente?.nombre ?? '—' }}</td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400">{{ vendedorNombre(op.pedido?.vendedor) }}</td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400">{{ formatFecha(op.pedido?.fecha_entrega) }}</td>
            <td class="px-4 py-3">
              <span :class="badgeEstado(op.estado)" class="text-xs font-semibold px-2 py-0.5 rounded-full">
                {{ labelEstado(op.estado) }}
              </span>
            </td>
            <!-- Columna nota -->
            <td class="px-4 py-3">
              <span v-if="op.nota" :class="badgeNota(op.nota.estado)" class="text-xs font-semibold px-2 py-0.5 rounded-full">
                {{ op.nota.folio }}
              </span>
              <span v-else class="text-gray-300 dark:text-gray-600 text-xs">—</span>
            </td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{{ formatFecha(op.created_at) }}</td>
            <td class="px-4 py-3">
              <div class="flex gap-2 justify-end flex-wrap">
                <!-- Ver hoja -->
                <button
                  @click="verHoja(op)"
                  title="Ver Hoja de Producción"
                  class="px-2.5 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-violet-100 hover:text-violet-700 transition text-xs font-medium"
                >
                  Ver
                </button>

                <!-- Iniciar -->
                <button
                  v-if="op.estado === 'activa'"
                  @click="cambiarEstado(op, 'en_proceso')"
                  class="px-2.5 py-1.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition text-xs font-medium"
                >
                  Iniciar
                </button>

                <!-- Completar -->
                <button
                  v-if="op.estado === 'activa' || op.estado === 'en_proceso'"
                  @click="cambiarEstado(op, 'completada')"
                  class="px-2.5 py-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition text-xs font-medium"
                >
                  Completar
                </button>

                <!-- Generar nota (solo si completada y sin nota activa/facturada) -->
                <button
                  v-if="op.estado === 'completada' && !op.nota"
                  @click="generarNota(op)"
                  :disabled="generandoNota === op.id"
                  class="px-2.5 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition text-xs font-semibold"
                >
                  {{ generandoNota === op.id ? 'Generando...' : 'Generar Nota' }}
                </button>

                <!-- Cancelar -->
                <button
                  v-if="op.estado !== 'cancelada' && op.estado !== 'completada'"
                  @click="cambiarEstado(op, 'cancelada')"
                  class="px-2.5 py-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200 transition text-xs font-medium"
                >
                  Cancelar
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="meta.last_page > 1" class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
      <span>Página {{ meta.current_page }} de {{ meta.last_page }} — {{ meta.total }} resultados</span>
      <div class="flex gap-2">
        <button @click="cargar(meta.current_page - 1)" :disabled="meta.current_page <= 1"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Anterior</button>
        <button @click="cargar(meta.current_page + 1)" :disabled="meta.current_page >= meta.last_page"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Siguiente</button>
      </div>
    </div>

    <!-- Modal hoja de producción -->
    <ModalHojaProduccion
      :visible="modalHoja.visible"
      :orden="modalHoja.orden"
      @cerrar="modalHoja.visible = false"
    />

    <!-- Confirmación generar nota -->
    <div v-if="confirmNota.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6 text-center">
        <div class="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-1">Generar Nota de Venta</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-1">Orden: <span class="font-semibold text-gray-700 dark:text-gray-300">{{ confirmNota.orden?.numero_orden }}</span></p>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">Cliente: <span class="font-semibold text-gray-700 dark:text-gray-300">{{ confirmNota.orden?.pedido?.cliente?.nombre }}</span></p>
        <div class="flex gap-3">
          <button @click="confirmNota.visible = false" class="flex-1 px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            Cancelar
          </button>
          <button @click="confirmarGenerarNota" :disabled="generandoNota !== null" class="flex-1 px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition">
            {{ generandoNota !== null ? 'Generando...' : 'Sí, generar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal nota generada -->
    <ModalNota
      :visible="modalNota.visible"
      :nota="modalNota.nota"
      @cerrar="modalNota.visible = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import ModalHojaProduccion from './ModalHojaProduccion.vue'
import ModalNota from './ModalNota.vue'

const http        = useHttp()
const httpOp      = useHttp()
const httpNota    = useHttp()

const ordenes      = ref([])
const cargando     = ref(false)
const generandoNota = ref(null)
const meta         = ref({ current_page: 1, last_page: 1, total: 0 })
const filtros      = ref({ buscar: '', estado: '' })

const modalHoja    = ref({ visible: false, orden: {} })
const modalNota    = ref({ visible: false, nota: {} })
const confirmNota  = ref({ visible: false, orden: null })

const cargar = async (pagina = 1) => {
  cargando.value = true
  try {
    const params = new URLSearchParams({ page: pagina, per_page: 15 })
    if (filtros.value.buscar) params.set('buscar', filtros.value.buscar)
    if (filtros.value.estado) params.set('estado', filtros.value.estado)

    await http.get(`/pos2/ordenes-produccion?${params}`)
    const res = http.data.value
    ordenes.value = res.data ?? []
    meta.value    = { current_page: res.current_page, last_page: res.last_page, total: res.total }
  } finally {
    cargando.value = false
  }
}

const limpiar = () => {
  filtros.value = { buscar: '', estado: '' }
  cargar(1)
}

const verHoja = (op) => {
  modalHoja.value = { visible: true, orden: op }
}

const cambiarEstado = async (op, nuevoEstado) => {
  try {
    await httpOp.patch(`/pos2/ordenes-produccion/${op.id}/estado`, { estado: nuevoEstado })
    const actualizado = httpOp.data.value
    const idx = ordenes.value.findIndex(o => o.id === op.id)
    if (idx !== -1) ordenes.value[idx] = { ...ordenes.value[idx], ...actualizado }
  } catch (err) {
    alert(err.response?.data?.message ?? 'Error al cambiar estado')
  }
}

const generarNota = (op) => {
  confirmNota.value = { visible: true, orden: op }
}

const confirmarGenerarNota = async () => {
  const op = confirmNota.value.orden
  generandoNota.value = op.id
  try {
    await httpNota.post(`/pos2/ordenes-produccion/${op.id}/nota`, {})
    const nota = httpNota.data.value
    const idx = ordenes.value.findIndex(o => o.id === op.id)
    if (idx !== -1) ordenes.value[idx] = { ...ordenes.value[idx], nota }
    confirmNota.value.visible = false
    modalNota.value = { visible: true, nota }
  } catch (err) {
    alert(err.response?.data?.message ?? 'Error al generar la nota')
  } finally {
    generandoNota.value = null
  }
}

const vendedorNombre = (v) => v ? `${v.nombre} ${v.apellido_paterno ?? ''}`.trim() : '—'

const formatFecha = (val) => {
  if (!val) return '—'
  return new Date(val).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
}

const labelEstado = (e) => ({
  activa:     'Activa',
  en_proceso: 'En Proceso',
  completada: 'Completada',
  cancelada:  'Cancelada',
}[e] ?? e)

const badgeEstado = (e) => ({
  activa:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  en_proceso: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  completada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelada:  'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
}[e] ?? 'bg-gray-100 text-gray-600')

const badgeNota = (e) => ({
  activa:    'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  facturada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelada: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
}[e] ?? 'bg-gray-100 text-gray-600')

onMounted(() => cargar(1))
</script>
